import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Star, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, media } from "../lib/api";

export async function uploadFile(file, kind = "image") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const { data } = await adminApi.post("/upload", fd);
  return data;
}

const CHUNK = 8 * 1024 * 1024;

export async function uploadChunked(file, onProgress) {
  const id = Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, "0")).join("");
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", id);
    fd.append("index", String(i));
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "part");
    for (let attempt = 1; ; attempt++) {
      try {
        await adminApi.post("/upload-chunk", fd, { timeout: 120000 });
        break;
      } catch (e) {
        if (attempt >= 4) throw e;
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const { data } = await adminApi.post("/upload-finalize", { upload_id: id, filename: file.name, content_type: file.type || "application/octet-stream", total });
  return data;
}

export const useList = (url) => {
  const [list, setList] = useState(null);
  const load = useCallback(() => adminApi.get(url).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [url]);
  useEffect(() => {
    load();
  }, [load]);
  return [list, load, setList];
};

export const Field = ({ label, textarea, testid, className = "", ...rest }) => (
  <label className={`block text-sm ${className}`}>
    <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#6E7565]">{label}</span>
    {textarea ? <textarea className="input-soft min-h-[110px]" data-testid={testid} {...rest} /> : <input className="input-soft" data-testid={testid} {...rest} />}
  </label>
);

export const Toggle = ({ label, checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid} className="flex items-center gap-3 text-sm">
    <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-[#7A8460]" : "bg-[#E2DACB]"}`}>
      <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </span>
    {label}
  </button>
);

export const Card = ({ title, children, action, className = "" }) => (
  <section className={`rounded-3xl border bg-white p-5 sm:p-6 ${className}`}>
    {(title || action) && <div className="mb-4 flex items-center justify-between gap-3"><h3 className="h-section text-xl">{title}</h3>{action}</div>}
    {children}
  </section>
);

export const PageTitle = ({ title, sub, action }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
    <div><h1 className="h-display text-3xl sm:text-4xl" data-testid="admin-page-title">{title}</h1>{sub && <p className="mt-1 text-sm text-[#6E7565]">{sub}</p>}</div>
    {action}
  </div>
);

export const ImageInput = ({ value, onChange, testid, className = "h-36 w-full" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      onChange((await uploadFile(f)).path);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <button type="button" onClick={() => ref.current.click()} data-testid={testid} className={`group relative grid place-items-center overflow-hidden rounded-2xl border-2 border-dashed border-[#A3AC86] bg-[#F7F3EA] ${className}`}>
      {value ? <img src={media(value)} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
      <span className={`relative flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold ${value ? "opacity-0 group-hover:opacity-100" : ""} transition-opacity`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {value ? "Změnit obrázek" : "Nahrát obrázek"}
      </span>
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} />
    </button>
  );
};

export const MultiImages = ({ value = [], onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) paths.push((await uploadFile(f)).path);
      onChange((cur) => [...cur, ...paths]);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
    e.target.value = "";
  };
  const move = (i, d) => {
    const n = [...value];
    [n[i], n[i + d]] = [n[i + d], n[i]];
    onChange(n);
  };
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
      {value.map((p, i) => (
        <div key={p + i} className="group relative aspect-square overflow-hidden rounded-2xl border" data-testid={`product-image-${i}`}>
          <img src={media(p)} alt="" className="h-full w-full object-cover" />
          {i === 0 && <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-[#F2C230] px-2 py-0.5 text-[10px] font-bold"><Star className="h-3 w-3" /> Hlavní</span>}
          <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/40 p-1 opacity-0 transition-opacity group-hover:opacity-100">
            {i > 0 && <button type="button" onClick={() => move(i, -1)} className="rounded-full bg-white p-1"><ArrowUp className="h-3 w-3 -rotate-90" /></button>}
            {i < value.length - 1 && <button type="button" onClick={() => move(i, 1)} className="rounded-full bg-white p-1"><ArrowDown className="h-3 w-3 -rotate-90" /></button>}
            <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="rounded-full bg-white p-1 text-[#D96B43]" data-testid={`product-image-remove-${i}`}><Trash2 className="h-3 w-3" /></button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => ref.current.click()} className="grid aspect-square place-items-center rounded-2xl border-2 border-dashed border-[#A3AC86] bg-[#F7F3EA] text-xs font-semibold" data-testid="product-images-upload">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="flex flex-col items-center gap-1"><Upload className="h-5 w-5" /> Přidat fotky</span>}
      </button>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={pick} />
    </div>
  );
};
